// Pushing a content doc into the zustand stores — the ONE place that happens, for both the boot load
// and a live update.
//
// Extracted from useContentLoader so `Jute.applyContentUpdate()` can reuse it. Reachable outside React,
// so it reads dispatchers via `getState()` rather than the hooks.
//
// THE DANGEROUS PART IS WHAT AN UPDATE MUST *NOT* REDO. The boot path deliberately resets scene state:
// it hides every scene and points the active scene at the first child. Re-running that on every edit
// would throw the user back to scene 1 and blank whatever they were looking at. Those steps are gated on
// `initial` — read the guards below before adding anything to this file.

import { Jute } from '.';
import { filestore, FsAudio, FsImageTarget } from '@/filestore';
import { isSafariWithAudioContextBug } from '@r3f/utils/general';
import { addMediaElementToAudioStreamDestination } from '@r3f/utils/audio-manager';
import { TransitionManager } from '@r3f/transitions/transitionManager';
import {
    IContentDoc,
    IRoot,
    ISceneComp,
    ITrackingContentDocByTrackingType
} from '@r3f-component-data-structure';
import { reconcileComponentsById, IComponentsById } from './content-reconcile';
import { bkgrAudio } from './zustand';
import { IDataActions, ITrackingImageDict, useDataStore } from './zustand/data-store';
import { ISceneActions, ITransCatDict, ITransCatTypes, useSceneStore } from './zustand/scene-store';

/**
 * Per-scene transition registries, keyed by scene id.
 *
 * Module-level and never cleared: `EntityLevelFadeGroup` and `ArSceneContent` register into
 * `transitionManager[sceneId]` during render, so an entry must exist BEFORE a scene's children mount.
 * A live update therefore adds entries for new scenes and leaves existing ones untouched — replacing one
 * would orphan every transition already registered against it.
 */
export const transitionManager: { [sceneId: string]: TransitionManager<any> } = {};

/** The `componentsById` last pushed, so the next update can preserve identity against it. */
let lastComponentsById: IComponentsById | null = null;

/** `bkgrSound.filestoreId` last wired up, so an update only re-wires when the track actually changed. */
let lastBkgrSoundFilestoreId: string | null = null;

/**
 * Whether `bkgrAudio` has been routed into the audio graph.
 *
 * Deliberately not cleared by `resetContentApplyState`: `createMediaElementSource` binds to the element
 * for its lifetime, and `bkgrAudio` is a module-level singleton that outlives any content reset. Clearing
 * this would let a second call through and throw.
 */
let bkgrAudioRoutedToGraph = false;

export interface IApplyContentOptions {
    /** True for the one-time boot load, false for a live update. Gates every scene-state reset. */
    initial: boolean;
}

export interface IApplyContentResult {
    componentsById: IComponentsById;
    rootId: string;
    tracking: ITrackingContentDocByTrackingType | undefined;
}

/** Forget the identity/audio watermarks. For teardown paths and tests — not used in normal operation. */
export function resetContentApplyState(): void {
    lastComponentsById = null;
    lastBkgrSoundFilestoreId = null;
}

/**
 * Push `contentData` into the data and scene stores.
 *
 * Assumes every referenced filestore id is already registered (`filestore.load` on boot,
 * `filestore.merge` on an update) — asset URLs are resolved from it here and in the preloader.
 */
export function applyContentToStores(contentData: IContentDoc, { initial }: IApplyContentOptions): IApplyContentResult {
    const dataDispatch = useDataStore.getState().dispatch;
    const sceneDispatch = useSceneStore.getState().dispatch;

    const {
        componentsById: incomingComponentsById,
        rootComponentId,
        tracking,
        webEmbed,
        shadowsEnabled = true,
        device = null,
        screenRelativeScaleType = 'height-relative'
    } = contentData;

    const root = incomingComponentsById[rootComponentId] as IRoot;

    // Reuse the previous object for every unchanged entity, so only genuinely edited ones re-render.
    const { componentsById, reused, changed, added, removed } = reconcileComponentsById(
        initial ? null : lastComponentsById,
        incomingComponentsById as IComponentsById
    );
    if (!initial) {
        console.log(
            `[jute-live] content applied: ${changed.length} changed, ${added.length} added, ${removed.length} removed, ${reused.length} unchanged`,
            { changed, added, removed }
        );
    }
    lastComponentsById = componentsById;

    const transCatDict: ITransCatDict = {};
    root.children.forEach((sceneId, i) => {
        // Only ever CREATE a missing registry. See the note on `transitionManager` above.
        if (!transitionManager[sceneId]) transitionManager[sceneId] = new TransitionManager();

        // Scene visibility is boot-time state. Rebuilding it on an update would hide the scene the user
        // is currently looking at.
        if (initial) {
            transCatDict[sceneId] = Jute.usesOrbitCamera ? (i === 0 ? ITransCatTypes.enter : ITransCatTypes.hidden) : ITransCatTypes.hidden;
        }

        // Titles are just a lookup table, safe (and necessary) to refresh on every apply.
        Jute.sceneTitleById[sceneId] = (componentsById[sceneId] as ISceneComp).title;
    });

    dataDispatch({
        type: IDataActions.ADD_CONTENT,
        componentsById,
        webEmbed,
        shadowsEnabled,
        webXR: device,
        screenRelativeScaleType
    });

    dataDispatch({
        type: IDataActions.ADD_ROOT_ID,
        rootId: rootComponentId
    });

    _applyBackgroundAudio(root);

    if (initial) {
        sceneDispatch?.({
            type: ISceneActions.SET_ACTIVE_SCENE_ID,
            activeSceneId: root.children[0]
        });
        sceneDispatch?.({
            type: ISceneActions.SET_TRANSITION_CATEGORY,
            transCatDict
        });
    }

    return { componentsById, rootId: rootComponentId, tracking };
}

/**
 * Wire up the project's background audio — but only when the track actually changed.
 *
 * `bkgrAudio` is a single module-level element that may be mid-playback. Re-assigning `src` to the same
 * URL restarts it, so an unconditional call would audibly restart the music on every edit.
 *
 * Reusing that one element is what makes a live track swap possible at all: it was blessed for playback
 * by the user's launch tap, and an element keeps that blessing across a `src` change, so the new track
 * can start without a fresh gesture. A replacement element would be unblessed and silent. The same
 * reasoning drives the video swap in `useHlsLoader`, and it carries the same two obligations, both of
 * which used to be missed here.
 */
function _applyBackgroundAudio(root: IRoot): void {
    const filestoreId = typeof root?.bkgrSound?.filestoreId === 'string' ? root.bkgrSound.filestoreId : null;
    if (filestoreId === lastBkgrSoundFilestoreId) return;
    lastBkgrSoundFilestoreId = filestoreId;
    if (!filestoreId) return;

    // Obligation 1: assigning `src` pauses the element while the new resource loads, and nothing here
    // restarted it — so swapping the track while the music was playing left it silent for the rest of the
    // session. Captured before the assignment below, resumed once the new track has data.
    const wasPlaying = !bkgrAudio.paused;

    bkgrAudio.loop = !!root?.bkgrSound?.loop;
    bkgrAudio.crossOrigin = 'anonymous';
    const audioFile = filestore.get<FsAudio>(filestoreId);
    bkgrAudio.src = audioFile.getMp3Url();

    // Obligation 2: route into the audio graph exactly once. `createMediaElementSource` throws
    // `InvalidStateError` if it runs twice on the same element, and this function runs on live updates as
    // well as the boot load — so the unguarded call threw on the first background-track swap and took the
    // rest of the content-apply pass down with it. The existing routing still applies to the new source.
    if (!isSafariWithAudioContextBug() && !bkgrAudioRoutedToGraph) {
        bkgrAudioRoutedToGraph = true;
        addMediaElementToAudioStreamDestination(Jute.audioContext, Jute.audioStreamDestination, bkgrAudio);
        // Connecting can nudge the element into playing; undo that unless it was already running.
        if (!wasPlaying) {
            bkgrAudio.pause();
            bkgrAudio.currentTime = 0;
        }
    }

    if (wasPlaying) {
        bkgrAudio.addEventListener('loadeddata', () => {
            // No fresh gesture needed — the element kept its blessing. Can still reject if another load
            // interrupts this one, which is harmless.
            void bkgrAudio.play().catch(() => undefined);
        }, { once: true });
    }
}

/**
 * Fetch the ZPT tracking data for every image target and push it into the store.
 *
 * NOT re-run by a live update: the tracker consumes these targets at init, so swapping them under a
 * running camera is out of scope (a tracking change is classified as needing a reload instead).
 */
export async function applyImageTrackingData(tracking: ITrackingContentDocByTrackingType | undefined): Promise<void> {
    if (!tracking?.['image']) return;
    const dataDispatch = useDataStore.getState().dispatch;
    const trkDict = tracking['image'];
    const ids = Object.keys(trkDict);
    const trackingImageDict: ITrackingImageDict = {};

    for (let i = 0; i < ids.length; i++) {
        const trackingImageData = trkDict[ids[i]];
        if (!trackingImageData.filestoreId) continue;
        const fsFile = filestore.get<FsImageTarget>(trackingImageData.filestoreId);
        const trackingImageFile = await fsFile.getZpt();
        const trackingImageUrl = fsFile.getImageUrl();
        if (!trackingImageFile) continue;
        trackingImageDict[trackingImageData.id] = { ...trackingImageData, file: trackingImageFile, imageUrl: trackingImageUrl };
    }

    dataDispatch({ type: IDataActions.ADD_CONTENT, trackingImageDict });
}
